import { coreDelete, corePatch, corePost } from "@/services/coreApi"
import { collectLegFuelData } from "@/features/route/state/RouteAdapters"

const lonlatIsValid = (lonlat) =>
  Array.isArray(lonlat) &&
  lonlat.length === 2 &&
  Number.isFinite(lonlat[0]) &&
  Number.isFinite(lonlat[1])

export const createVessel = async ({
  accessContext,
  setAccessContext,
  customerId,
  accountId,
  name,
  imo,
  active = true,
}) => {
  const data = await corePost(
    "/vessels",
    {
      customerId,
      accountId,
      name,
      imo: imo ?? "",
      active,
    },
    accessContext,
    setAccessContext,
    ".createVessel error=",
  )
  return data.vessel
}

const toIsoTimestamp = (raw) => {
  if (Number.isFinite(raw)) {
    return new Date(raw > 1e12 ? raw : raw * 1000).toISOString()
  }
  if (typeof raw === "string" && raw.trim()) {
    const parsedMs = Date.parse(raw)
    if (!Number.isNaN(parsedMs)) return new Date(parsedMs).toISOString()
  }
  return undefined
}

const toLonlat = (coords) => {
  const lonlat = Array.isArray(coords) ? [Number(coords[0]), Number(coords[1])] : null
  return lonlatIsValid(lonlat) ? lonlat : undefined
}

// newVoyage block for POST /routes/planned and /routes/import.
export const buildNewVoyage = ({ name, features = [], etd, eta, baseSpeedMs }) => {
  const voyage = {
    name,
    lonlatFrom: toLonlat(features[0]?.geometry?.coordinates),
    lonlatTo: toLonlat(features.at(-1)?.geometry?.coordinates),
    etdCw: toIsoTimestamp(etd),
    etaCw: toIsoTimestamp(eta),
    basespeedMsec: Number.isFinite(baseSpeedMs) ? [baseSpeedMs] : undefined,
  }
  for (const key of Object.keys(voyage)) {
    if (voyage[key] === undefined) delete voyage[key]
  }
  return voyage
}

const toPlannedWaypoint = (waypoint, waypointNumber, baseSpeed) => {
  // The DB column has no default: fail the whole save before anything is sent.
  const timestamp = toIsoTimestamp(waypoint?.timestamp ?? waypoint?.calculatedTimestamp ?? null)
  if (!timestamp) {
    throw new Error(
      `Cannot save waypoint ${waypointNumber}: missing or invalid timestamp.`,
    )
  }
  const lon = Number(waypoint?.lon ?? waypoint?.geometry?.coordinates?.[0])
  const lat = Number(waypoint?.lat ?? waypoint?.geometry?.coordinates?.[1])
  // Legacy voyage file convention: legType 0 = greatcircle, 1 = rhumbline.
  // Pathfinder/DB convention: string "greatcircle" or "rhumbline".
  const legTypeRaw = waypoint?.legType ?? waypoint?.legtype
  const legtypeStr = String(legTypeRaw).toLowerCase()
  const legtype =
    Number(legTypeRaw) === 0 || legtypeStr === "greatcircle"
      ? "greatcircle"
      : "rhumbline"

  return {
    lonlat: [lon, lat],
    timestamp,
    legtype,
    ...(Number.isFinite(baseSpeed) ? { basespeed: baseSpeed } : {}),
    ...(waypoint?.type ? { type: waypoint.type } : {}),
    ...extractPathfinderFields(waypoint),
  }
}

// Extract pathfinder response fields from a waypoint feature properties object.
// Returns only keys whose value is defined; empty object for non-pathfinder waypoints.
const extractPathfinderFields = (waypoint) => {
  if (!waypoint) return {}

  const result = {}
  const copyNumber = (srcKey, dstKey) => {
    const value = waypoint[srcKey]
    if (typeof value === "number" && Number.isFinite(value)) {
      result[dstKey] = value
    }
  }

  // Accept both snake_case (flat astar) and camelCase (normalized) keys
  copyNumber("sog", "sog")
  copyNumber("sog_knots", "sog")
  copyNumber("heading", "heading")
  copyNumber("bearing_to_next_waypoint_deg_north", "heading")
  copyNumber("baseSpeedAdjustedKmh", "baseSpeedAdjustedKmh")
  copyNumber("base_speed_adjusted_kmh", "baseSpeedAdjustedKmh")
  copyNumber("legDistanceKm", "legDistanceKm")
  copyNumber("leg_distance_km", "legDistanceKm")
  copyNumber("legTimeHour", "legTimeHour")
  copyNumber("leg_time_hour", "legTimeHour")
  copyNumber("legSegmentDistanceKm", "legSegmentDistanceKm")
  copyNumber("leg_segment_distance_km", "legSegmentDistanceKm")
  copyNumber("legSegmentTimeHour", "legSegmentTimeHour")
  copyNumber("leg_segment_time_hour", "legSegmentTimeHour")
  copyNumber("sumDistanceKm", "sumDistanceKm")
  copyNumber("sum_distance_km", "sumDistanceKm")
  copyNumber("sumTimeHour", "sumTimeHour")
  copyNumber("sum_time_hour", "sumTimeHour")

  // JSONB snapshot blobs — grouped from the flat pathfinder response
  const weatherSnapshot = {}
  const rnwEvaluation = {}
  const restrictions = {}
  const warnings = {}

  for (const [key, value] of Object.entries(waypoint)) {
    if (value === undefined) continue
    if (
      key.startsWith("wind_") ||
      key.startsWith("wave_") ||
      key.startsWith("current_")
    ) {
      weatherSnapshot[key] = value
    } else if (
      key === "ww_nav_cat" ||
      key === "ww_is_free_vessel_vertical_clearance" ||
      key === "ww_is_free_usage" ||
      key === "ww_is_closed_viaarea" ||
      key === "ww_is_leg_usable" ||
      key === "ww_maxspeed_knots" ||
      key === "ww_maxspeed_kmh" ||
      key === "ww_minspeed_knots" ||
      key === "ww_minspeed_kmh"
    ) {
      rnwEvaluation[key] = value
    } else if (
      key === "ww_parse_tree" ||
      key === "ww_parse_tree_result" ||
      key === "ww_parse_tree_substitution" ||
      key === "ww_restriction_text_full" ||
      key === "ww_restriction_text_single" ||
      key === "ww_restriction_text_json" ||
      key === "ww_restriction_text_check"
    ) {
      restrictions[key] = value
    } else if (key.startsWith("warning_")) {
      warnings[key] = value
    }
  }

  // Prefer pre-grouped blobs (normalized data), fall back to rebuilt from flat keys
  result.weatherSnapshot =
    (waypoint.weather_snapshot && typeof waypoint.weather_snapshot === "object") ? waypoint.weather_snapshot :
    (waypoint.weatherSnapshot && typeof waypoint.weatherSnapshot === "object") ? waypoint.weatherSnapshot :
    Object.keys(weatherSnapshot).length > 0 ? weatherSnapshot : undefined
  result.rnwEvaluation =
    (waypoint.rnw_evaluation && typeof waypoint.rnw_evaluation === "object") ? waypoint.rnw_evaluation :
    (waypoint.rnwEvaluation && typeof waypoint.rnwEvaluation === "object") ? waypoint.rnwEvaluation :
    Object.keys(rnwEvaluation).length > 0 ? rnwEvaluation : undefined
  result.restrictions =
    (waypoint.restrictions && typeof waypoint.restrictions === "object") ? waypoint.restrictions :
    Object.keys(restrictions).length > 0 ? restrictions : undefined
  result.warnings =
    (waypoint.warnings && typeof waypoint.warnings === "object") ? waypoint.warnings :
    Object.keys(warnings).length > 0 ? warnings : undefined
  // Merge flat astar fuel keys in — covers save paths that skip normalizeWaypoint
  const legBlob =
    (waypoint.leg_data && typeof waypoint.leg_data === "object") ? waypoint.leg_data :
    (waypoint.legData && typeof waypoint.legData === "object") ? waypoint.legData : undefined
  const legFuel = collectLegFuelData(waypoint)
  result.legData = legFuel ? { ...(legBlob ?? {}), ...legFuel } : legBlob

  // Remove undefined keys so they don't get sent as null
  for (const key of Object.keys(result)) {
    if (result[key] === undefined) delete result[key]
  }

  return result
}

// With `newVoyage` core creates and activates the voyage only once route + waypoints are stored.
export const savePlannedRoute = async ({
  accessContext,
  setAccessContext,
  vesselId,
  voyageId = undefined,
  newVoyage = undefined,
  name,
  subtype = "Meteo Tracker",
  baseSpeed = undefined,
  waypoints = [],
  jsonRequest = undefined,
  summary = undefined,
  optimisedFor = undefined,
}) => {
  const body = {
    vesselId,
    name,
    subtype,
    waypoints: waypoints.map((wp, i) => toPlannedWaypoint(wp, i, baseSpeed)),
  }
  if (newVoyage) body.newVoyage = newVoyage
  else body.voyageId = voyageId
  if (jsonRequest && typeof jsonRequest === "object") body.jsonRequest = jsonRequest
  if (summary && typeof summary === "object") body.summary = summary
  if (optimisedFor) body.optimisedFor = optimisedFor

  return corePost(
    "/routes/planned",
    body,
    accessContext,
    setAccessContext,
    ".savePlannedRoute error=",
  )
}

export const deleteRoute = async ({
  accessContext,
  setAccessContext,
  routeId,
}) => {
  const data = await coreDelete(
    `/routes/${routeId}`,
    accessContext,
    setAccessContext,
    ".deleteRoute error=",
  )
  return data
}

export const deleteVoyage = async ({
  accessContext,
  setAccessContext,
  voyageId,
}) => {
  const data = await coreDelete(
    `/voyages/${voyageId}`,
    accessContext,
    setAccessContext,
    ".deleteVoyage error=",
  )
  return data
}

export const updateRoute = async ({
  accessContext,
  setAccessContext,
  routeId,
  patch,
}) => {
  const data = await corePatch(
    `/routes/${routeId}`,
    patch,
    accessContext,
    setAccessContext,
    ".updateRoute error=",
  )
  return data?.route ?? data
}

export const updateVoyage = async ({
  accessContext,
  setAccessContext,
  voyageId,
  patch,
}) => {
  const data = await corePatch(
    `/voyages/${voyageId}`,
    patch,
    accessContext,
    setAccessContext,
    ".updateVoyage error=",
  )
  return data?.voyage ?? data
}

// Activate a route; backend archives the previous active sibling.
export const activateRoute = async ({
  accessContext,
  setAccessContext,
  routeId,
}) => {
  const data = await corePost(
    `/routes/${routeId}/activate`,
    {},
    accessContext,
    setAccessContext,
    ".activateRoute error=",
  )
  return data?.route ?? data
}

// Make a planned voyage the single active one for its vessel.
export const activateVoyage = async ({
  accessContext,
  setAccessContext,
  voyageId,
}) => {
  const data = await corePost(
    `/voyages/${voyageId}/activate`,
    {},
    accessContext,
    setAccessContext,
    ".activateVoyage error=",
  )
  return data?.voyage ?? data
}
